<template>
  <div>
    <a
      @click.prevent="castVote()"
      href="#"
      class="text-neutral-600 text-lg flex-shrink-0 hover:no-underline flex flex-col items-center justify-center mr-4 w-16 py-4 border border-neutral-200 rounded-xl h-full"
      :class="{
        'text-project-500': alreadyVoted,
        'vote-closed': votesDisabled,
      }"
      v-tippy="{ placement: 'top', content: $t('Voting is closed for this idea'), onShow: () => votesDisabled }"
    >
        <svg
        class="w-3 h-3 mb-2 rotate-180 transition-transform duration-200 ease-in-out"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        viewBox="0 0 30 30"
        :class="{ '-translate-y-0.5': isAnimating }"
      >
        <path
          d="M15,23c-0.256,0-0.512-0.098-0.707-0.293l-10-10c-0.286-0.286-0.372-0.716-0.217-1.09C4.23,11.243,4.596,11,5,11h20 c0.404,0,0.77,0.243,0.924,0.617c0.155,0.374,0.069,0.804-0.217,1.09l-10,10C15.512,22.902,15.256,23,15,23z"
        ></path>
      </svg>
      <div
        class="font-bold leading-none text-base -mt-1 mb-1"
        v-if="!allowVoteCount || currentProjectAdmin"
        id="post-upvote-count"
      >
        {{ upvotes }}
        </div>
    </a>
  </div>
</template>

<script>
import EventBus from "event_bus";

export default {
  props: ["current-user", "original-post", 'allow-anonymous', "allow-vote-count", "current-project-admin"],
  data: function () {
    return {
      post: this.originalPost,
      upvotes:
        this.originalPost.cached_votes_up + this.originalPost.manual_upvotes,
      alreadyVoted: this.originalPost.current_user_voted_for,
      isVoting: false,
      isAnimating: false,
      votesDisabled: !!this.originalPost.votes_disabled,
    };
  },
  mounted () {
    EventBus.$on("updateUpvotes", (post) => {
      this.upvotes = post.cached_votes_up + post.manual_upvotes
    });
    EventBus.$on("post_status_changed", (status) => {
      if (typeof status.disable_votes !== "undefined") {
        this.votesDisabled = !!status.disable_votes;
      }
    });
  },
  methods: {
    castVote: async function (recaptchaToken = null) {
      if (this.isVoting || this.votesDisabled) return;

      // Handle unauthorized users
      if (!this.allowAnonymous && !this.currentUser) {
        EventBus.$emit("openRegModalSignup");
        return;
      }

      this.isVoting = true;
      // Only animate when adding a vote, not removing
      if (!this.alreadyVoted) {
        this.isAnimating = true;
        setTimeout(() => {
          this.isAnimating = false;
        }, 300);
      }

      // Optimistically update UI
      const previousVoteState = this.alreadyVoted;
      const voteChange = previousVoteState ? -1 : 1;
      this.alreadyVoted = !previousVoteState;
      this.upvotes += voteChange;

      try {
        let vote_post_url = "/boards/" + this.post.board_id + "/posts/" + this.post.id + "/vote";

        // TEST MODE: Pass test_fraud param from URL if present (development only)
        const urlParams = new URLSearchParams(window.location.search);
        const testFraud = urlParams.get('test_fraud');
        if (testFraud) {
          vote_post_url += "?test_fraud=" + testFraud;
        }

        const data = new FormData();

        // Add reCAPTCHA token if provided
        if (recaptchaToken) {
          data.append("recaptcha_token", recaptchaToken);
        }

        const response = await new Promise((resolve, reject) => {
          Rails.ajax({
            url: vote_post_url,
            type: "POST",
            data: data,
            dataType: "json",
            success: resolve,
            error: (xhr) => {
              // Parse error response
              if (xhr.status === 429) {
                reject({ rate_limited: true, message: "Too many requests. Please try again later." });
              } else if (xhr.responseJSON) {
                reject(xhr.responseJSON);
              } else {
                reject({ error: "Request failed" });
              }
            },
          });
        });

        // Handle server response
        if (response.restricted_domain) {
          // Revert optimistic updates if domain is restricted
          this.alreadyVoted = previousVoteState;
          this.upvotes -= voteChange;
          return;
        }

        // Handle captcha requirement
        if (response.require_captcha && !recaptchaToken) {
          // Revert optimistic updates, get captcha, retry
          this.alreadyVoted = previousVoteState;
          this.upvotes -= voteChange;
          this.isVoting = false;
          await this.castVoteWithCaptcha();
          return;
        }

        if (typeof response.votes !== 'undefined') {
          // Update with server's vote count
          this.upvotes = response.votes;
        }

        // Keep the admin voter list in sync with the confirmed vote toggle
        if (this.currentUser && this.currentUser.id) {
          if (previousVoteState) {
            EventBus.$emit("currentUserUnvoted", this.currentUser.id);
          } else {
            EventBus.$emit("currentUserVoted", this.currentUser);
          }
        }

      } catch (error) {
        // Revert optimistic updates on error
        this.alreadyVoted = previousVoteState;
        this.upvotes -= voteChange;

        if (error.require_captcha && !recaptchaToken) {
          this.isVoting = false;
          await this.castVoteWithCaptcha();
          return;
        }

        if (error.rate_limited) {
          console.warn('Rate limited:', error.message);
        } else {
          console.error('Voting failed:', error);
        }
      } finally {
        this.isVoting = false;
      }
    },

    async castVoteWithCaptcha() {
      try {
        // Check if reCAPTCHA is loaded
        if (typeof grecaptcha === 'undefined' || typeof grecaptcha.enterprise === 'undefined') {
          console.error('reCAPTCHA not loaded');
          return;
        }

        await grecaptcha.enterprise.ready(async () => {
          const token = await grecaptcha.enterprise.execute(
            '6LdcSVssAAAAABZPe71VEzUDIQjv9qbrW-rIGeVY',
            { action: 'VOTE' }
          );
          await this.castVote(token);
        });
      } catch (error) {
        console.error('reCAPTCHA error:', error);
      }
    },
  },
};
</script>

<style scoped>
.vote-closed {
  cursor: default;
}

.vote-closed svg {
  opacity: 0.35;
}
</style>
